import { Trophy } from 'lucide-react';
import MedalBadge from './MedalBadge';
import { TeamLogo } from './TeamLogo';
import { roundLabel } from './TournamentBracket';

const GREEN = '#22C55E', RED = '#EF4444';

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// Champion team id for a decided bracket (mirrors champOf, returns the id).
function championId(matches, format) {
  const fin = matches
    .filter(m => (m.bracket_type === 'final_reset' || m.bracket_type === 'final') && m.status === 'completed' && m.winner_side)
    .sort((a, b) => b.round - a.round)[0]
    || (format === 'single_elimination' || !format ? (() => {
      const main = matches.filter(m => m.bracket_type !== 'third_place' && m.bracket_type !== 'losers');
      if (!main.length) return null;
      const mr = Math.max(...main.map(m => m.round || 0));
      return main.filter(m => (m.round || 0) === mr && m.status === 'completed' && m.winner_side)[0] || null;
    })() : null);
  if (!fin || !fin.winner_side) return null;
  return fin.winner_side === 'a' ? fin.team_a_id : fin.team_b_id;
}

// Bracket formats have no running W-L table like round robin, so derive a ranking
// from how far each team advanced: champion / decided places first, then teams
// still alive (deeper round first), then eliminated teams (later exit ranks
// higher). SE gets exact finishing places via the binary-tree math (a team out in
// round r ties at 2^(maxRound−r)+1); a third-place match refines 3rd/4th. DE
// non-final eliminations are ranked by depth (exact DE seeding lands with 2.2).
export function deriveBracketPlacings(matches, format) {
  const isDouble = format === 'double_elimination';
  const teams = new Map();
  const touch = (id, name) => {
    if (!id) return null;
    if (!teams.has(id)) teams.set(id, { team_id: id, team_name: name || 'TBD', wins: 0, losses: 0, maxRound: 0, lostRound: 0, lostFinal: false, lbLostRound: 0 });
    const t = teams.get(id); if (name) t.team_name = name; return t;
  };
  matches.forEach(m => {
    const a = touch(m.team_a_id, m.team_a_name), b = touch(m.team_b_id, m.team_b_name);
    [a, b].forEach(t => { if (t) t.maxRound = Math.max(t.maxRound, m.round || 0); });
  });
  matches.filter(m => m.status === 'completed' && m.winner_side).forEach(m => {
    if (m.bracket_type === 'third_place') return; // resolved separately
    const winId = m.winner_side === 'a' ? m.team_a_id : m.team_b_id;
    const loseId = m.winner_side === 'a' ? m.team_b_id : m.team_a_id;
    const w = teams.get(winId), l = teams.get(loseId);
    if (w) w.wins++;
    if (l) {
      l.losses++; l.lostRound = Math.max(l.lostRound, m.round || 0);
      if (m.bracket_type === 'final' || m.bracket_type === 'final_reset') l.lostFinal = true;
      if (m.bracket_type === 'losers') l.lbLostRound = Math.max(l.lbLostRound, m.round || 0); // the eliminating (2nd) loss in DE
    }
  });
  const mainRounds = matches.filter(m => m.bracket_type !== 'losers' && m.bracket_type !== 'third_place').map(m => m.round || 1);
  const maxRound = Math.max(1, ...mainRounds);
  const lbMax = Math.max(0, ...matches.filter(m => m.bracket_type === 'losers').map(m => m.round || 0));
  const champ = championId(matches, format);
  const bronze = matches.find(m => m.bracket_type === 'third_place' && m.status === 'completed' && m.winner_side);
  const bronzeWin = bronze ? (bronze.winner_side === 'a' ? bronze.team_a_id : bronze.team_b_id) : null;
  const bronzeLose = bronze ? (bronze.winner_side === 'a' ? bronze.team_b_id : bronze.team_a_id) : null;

  const rows = [...teams.values()].map(t => {
    const eliminated = isDouble ? (t.losses >= 2 || t.lostFinal) : (t.losses >= 1);
    let place = null;
    if (t.team_id === champ) place = 1;
    else if (t.team_id === bronzeWin) place = 3;
    else if (t.team_id === bronzeLose) place = 4;
    else if (eliminated) {
      if (t.lostFinal) place = 2;
      else if (!isDouble && t.lostRound === maxRound) place = 2; // SE final loser, no explicit final bracket_type
      else if (!isDouble && t.lostRound) place = Math.pow(2, Math.max(0, maxRound - t.lostRound)) + 1;
    }
    return {
      team_id: t.team_id, team_name: t.team_name, wins: t.wins, losses: t.losses,
      maxRound: t.maxRound, lostRound: t.lostRound, lbLostRound: t.lbLostRound, eliminated, alive: !eliminated, place,
    };
  });

  // Exact double-elim placings: below the two finalists, everyone was knocked out
  // in the losers bracket, and finish order is fixed by WHICH losers round did it
  // (LB final loser = 3rd, prior round = 4th, …). Compute it STRUCTURALLY — a team
  // out in round r finishes above every team eliminated in later LB rounds, so
  // place(r) = 3 + (LB matches in rounds after r). This is correct mid-event too
  // (the bracket shape is fixed), not just once everyone's eliminated. Ties (same
  // round) share a place automatically.
  if (isDouble) {
    const lbCount = {};
    matches.filter(m => m.bracket_type === 'losers').forEach(m => { lbCount[m.round] = (lbCount[m.round] || 0) + 1; });
    const placeForLb = (r) => 3 + Object.keys(lbCount).reduce((s, rr) => s + (Number(rr) > r ? lbCount[rr] : 0), 0);
    rows.forEach(r => { if (r.place == null && r.eliminated && r.lbLostRound) r.place = placeForLb(r.lbLostRound); });
  }

  rows.sort((a, b) => {
    if (a.place != null && b.place != null) return a.place - b.place;
    if (a.place != null) return -1;
    if (b.place != null) return 1;
    if (a.alive !== b.alive) return a.alive ? -1 : 1;           // alive above eliminated
    if (a.alive) return (b.maxRound - a.maxRound) || (b.wins - a.wins);
    return (b.lostRound - a.lostRound) || (b.wins - a.wins);    // eliminated: later exit first
  });

  const anyAlive = rows.some(r => r.alive);
  return { rows, maxRound, lbMax, decided: champ != null, completed: champ != null && !anyAlive };
}

const Card = ({ children, sticky }) => (
  <div className={`rounded-2xl overflow-hidden ${sticky ? 'lg:sticky lg:top-4' : ''}`} style={{ background: '#141414', border: '1px solid #262626' }}>{children}</div>
);
const Head = ({ title, meta }) => (
  <div className="px-4 py-3 flex items-center gap-1.5" style={{ background: '#1C1C1C', borderBottom: '1px solid #262626' }}>
    <Trophy className="h-4 w-4" style={{ color: '#F59E0B' }} />
    <h3 className="text-sm font-black uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: '#F59E0B' }}>{title}</h3>
    {meta && <span className="text-[10px] uppercase ml-auto" style={{ color: '#555', letterSpacing: '0.12em' }}>{meta}</span>}
  </div>
);
const GroupLabel = ({ children }) => (
  <div className="px-4 py-1.5 text-[9px] font-bold uppercase" style={{ letterSpacing: '0.14em', color: '#525252', background: '#101010', borderTop: '1px solid #1A1A1A' }}>{children}</div>
);
function Row({ rank, name, logo, sub, right, rightColor, onLogo, first }) {
  return (
    <div className="flex items-center gap-3 px-3.5 py-2.5" style={{ borderTop: first ? 'none' : '1px solid #1A1A1A' }}>
      <span className="shrink-0 flex items-center justify-center" style={{ width: 22 }}>
        {rank != null ? <MedalBadge rank={rank} size={22} /> : <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#1C1C1C', border: '1px solid #333', color: '#5B5B5B', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>–</span>}
      </span>
      <TeamLogo logo={logo} name={name} size={30} onClick={onLogo} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: '#fff' }}>{name}</p>
        {sub && <p className="text-[10px] truncate" style={{ color: '#666', marginTop: 1 }}>{sub}</p>}
      </div>
      {right != null && <span className="text-sm font-black tabular-nums shrink-0" style={{ color: rightColor || '#8A8A82', fontFamily: 'Oswald, sans-serif' }}>{right}</span>}
    </div>
  );
}

// Right-rail ranking panel — round-robin standings, or a derived bracket ranking
// (see deriveBracketPlacings). Modeled on the open-play live leaderboard.
export default function RankingPanel({ format, matches = [], standings, logoMap, onLogo, sticky = true, title }) {
  const logoOf = (id) => (logoMap && id ? logoMap[id] : null);

  // ── Round robin: reuse the standings rows ──
  if (format === 'round_robin') {
    const rows = standings || [];
    if (rows.length === 0) return null;
    return (
      <Card sticky={sticky}>
        <Head title={title || 'Standings'} meta="W · L · +/−" />
        {rows.map((r, i) => {
          const played = r.played ?? ((r.wins || 0) + (r.losses || 0));
          const rate = played ? Math.round((r.wins / played) * 100) : 0;
          return (
            <Row key={r.team_id} first={i === 0} rank={i + 1} name={r.team_name} logo={logoOf(r.team_id)} onLogo={onLogo}
              sub={`${played} played · ${r.wins || 0} won · ${rate}%`}
              right={`${r.diff > 0 ? '+' : ''}${r.diff}`} rightColor={r.diff > 0 ? GREEN : r.diff < 0 ? RED : '#8A8A82'} />
          );
        })}
      </Card>
    );
  }

  // ── Bracket formats: derived placings ──
  const { rows, maxRound, lbMax, decided } = deriveBracketPlacings(matches, format);
  if (rows.length === 0) return null;
  const placeCounts = {};
  rows.forEach(r => { if (r.place) placeCounts[r.place] = (placeCounts[r.place] || 0) + 1; });
  // Group by RESOLVED placement, not alive-status: a SE champion has 0 losses
  // (still "alive") but belongs with the placings, not the contenders.
  const contenders = rows.filter(r => r.place == null && r.alive);
  const ranked = rows.filter(r => r.place != null).sort((a, b) => a.place - b.place);
  const elimOther = rows.filter(r => r.place == null && r.eliminated);
  const resolved = [...ranked, ...elimOther]; // placed teams first, then DE exits without an exact seed
  const showGroups = contenders.length > 0 && resolved.length > 0; // in progress with a mix

  const aliveSub = (r) => `won ${r.wins} · ${r.maxRound >= maxRound ? 'in the final' : `reached ${roundLabel(r.maxRound, maxRound).toLowerCase()}`}`;
  const lbName = (rd) => (rd >= lbMax ? 'Losers Final' : `Losers R${rd}`);
  const tied = (r) => (placeCounts[r.place] > 1 ? 'tied ' : '');
  const goneSub = (r) => {
    if (r.place === 1) return `champion · ${r.wins} win${r.wins === 1 ? '' : 's'}`;
    if (r.place === 2) return `runner-up · ${r.wins} win${r.wins === 1 ? '' : 's'}`;
    if (r.lbLostRound) return `${tied(r)}${ordinal(r.place)} · lost ${lbName(r.lbLostRound)}`; // DE losers-bracket exit
    if (r.place === 3) return `3rd place · ${r.wins} win${r.wins === 1 ? '' : 's'}`;
    if (r.place) return `${tied(r)}${ordinal(r.place)} place · ${r.wins} win${r.wins === 1 ? '' : 's'}`;
    return `out in ${roundLabel(r.lostRound, maxRound).toLowerCase()}`; // no exact seed yet
  };

  return (
    <Card sticky={sticky}>
      <Head title={title || (decided ? 'Final placings' : 'Placings')} meta={decided ? null : 'live'} />
      {showGroups && <GroupLabel>Still in — {contenders.length} team{contenders.length === 1 ? '' : 's'}</GroupLabel>}
      {contenders.map((r, i) => (
        <Row key={r.team_id} first={i === 0 && !showGroups} rank={null} name={r.team_name} logo={logoOf(r.team_id)} onLogo={onLogo}
          sub={aliveSub(r)} right="Alive" rightColor={GREEN} />
      ))}
      {showGroups && <GroupLabel>Eliminated</GroupLabel>}
      {resolved.map((r, i) => (
        <Row key={r.team_id} first={i === 0 && !showGroups && contenders.length === 0} rank={r.place} name={r.team_name} logo={logoOf(r.team_id)} onLogo={onLogo}
          sub={goneSub(r)} right={r.wins > 0 ? `${r.wins}W` : null} rightColor={r.place === 1 ? '#FFD54A' : '#8A8A82'} />
      ))}
    </Card>
  );
}
